import { useState, useEffect } from 'react';
import { SectionScoring } from '../../components/tools/SectionScoring';
import { InfoSection } from '../../components/tools/InfoSection';
import { calculateSectionScore } from '../../utils/scoreCalculator';
import { motion } from 'framer-motion';
import { FAQ } from '../../components/tools/FAQ';
import { ScoringGuide } from '../../components/tools/ScoringGuide';
import { ScoreDisclaimer } from '../../components/tools/ScoreDisclaimer';
import { SEOHead } from '../../components/tools/SEOHead';
import Footer from '../../components/Footer';
import { Helmet } from 'react-helmet'; // Add this import if not already using Helmet

export function SATCalculator() {
  const [sections, setSections] = useState([
    {
      name: 'Math',
      modules: [
        { moduleNumber: 1, correctAnswers: 15, totalQuestions: 22, difficulty: 'medium' },
        { moduleNumber: 2, correctAnswers: 12, totalQuestions: 22, difficulty: 'medium' }
      ],
      scaledScore: 200
    },
    {
      name: 'Reading & Writing',
      modules: [
        { moduleNumber: 1, correctAnswers: 18, totalQuestions: 27, difficulty: 'medium' },
        { moduleNumber: 2, correctAnswers: 16, totalQuestions: 27, difficulty: 'medium' }
      ],
      scaledScore: 200
    }
  ]);

  useEffect(() => {
    setSections(prevSections => 
      prevSections.map(section => ({
        ...section,
        scaledScore: calculateSectionScore(section, section.modules)
      }))
    );
  }, []);

  const updateModuleScore = (sectionIndex, moduleIndex, updatedModule) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      newSections[sectionIndex].modules[moduleIndex] = updatedModule;
      newSections[sectionIndex].scaledScore = calculateSectionScore(
        newSections[sectionIndex],
        newSections[sectionIndex].modules
      );
      return newSections;
    });
  };

  const totalScore = sections.reduce((sum, section) => sum + section.scaledScore, 0);

  // Calculate percentile estimate based on total score (simplified example)
  const getPercentileEstimate = (score) => {
    if (score >= 1500) return "99th";
    if (score >= 1400) return "95th";
    if (score >= 1300) return "90th";
    if (score >= 1200) return "80th";
    if (score >= 1100) return "70th";
    if (score >= 1000) return "60th";
    if (score >= 900) return "50th";
    return "Below 50th";
  };

  const percentileEstimate = getPercentileEstimate(totalScore);

  return (
    <>
    <SEOHead
      title="Digital SAT Score Calculator 2025 | Free SAT Score Estimator Tool"
      description="Calculate your Digital SAT score instantly with our free adaptive scoring calculator. Get accurate estimates for Math and Reading & Writing sections based on official College Board scoring tables. Perfect for 2024-2025 Digital SAT test takers."
      keywords={[
        "SAT score calculator",
        "Digital SAT score calculator",
        "SAT digital score calculator",
        "2025 SAT scoring",
        "SAT Math calculator",
        "SAT Reading Writing calculator",
        "Digital SAT score estimation",
        "Free SAT calculator",
        "SAT score conversion",
        "College Board SAT scoring"
      ]}
      path="/tools/sat-score-calculator"
    />
    
    {/* Additional structured data for rich results */}
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Digital SAT Score Calculator",
          "description": "Free tool to calculate and estimate your Digital SAT score based on official College Board scoring tables",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        })}
      </script>
    </Helmet>
    
    <div className="max-w-5xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center my-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-3">Digital SAT Score Calculator 2025</h1>
          <p className="text-lg text-gray-600">Free tool using Official College Board Scoring Tables</p>
          <div className="mt-4 text-gray-600">
            <p>Estimate your SAT score instantly with our accurate Digital SAT calculator</p>
          </div>
        </motion.div>

        <div className="bg-blue-50 p-4 rounded-lg mb-8 border border-blue-200">
          <h2 className="text-xl font-semibold text-blue-800 mb-2">About This SAT Score Calculator</h2>
          <p>This free Digital SAT score calculator uses the latest College Board scoring algorithms to provide accurate estimates of your SAT score. Enter your correct answers for each module to calculate your total score on the 1600 scale.</p>
        </div>

        <InfoSection />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 my-8">
          {sections.map((section, sectionIndex) => (
            <SectionScoring
              key={sectionIndex}
              section={section}
              onModuleChange={(moduleIndex, updatedModule) => 
                updateModuleScore(sectionIndex, moduleIndex, updatedModule)
              }
            />
          ))}
        </div>

        <motion.section 
          className="card p-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white my-8 rounded-lg shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-3xl font-bold mb-6 text-center">Your Digital SAT Score</h2>
          <div className="flex flex-col items-center justify-center">
            <div className="text-8xl font-bold animate-pulse">
              {totalScore}
            </div>
            <p className="text-xl mt-4">Estimated Percentile: <span className="font-semibold">{percentileEstimate}</span></p>
          </div>
          <p className="text-center mt-6 text-blue-100">
            Based on College Board's Official 2024-2025 Digital SAT Adaptive Scoring Tables
          </p>
        </motion.section>

        <section className="my-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use This Digital SAT Score Calculator</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Enter the number of correct answers for each module in both Math and Reading & Writing sections</li>
            <li>The calculator automatically applies the College Board's adaptive scoring algorithm</li>
            <li>View your estimated section scores and total score on the 1600 scale</li>
            <li>Use this information to track your progress and set study goals</li>
          </ol>
        </section>

        <ScoringGuide />

        <section className="my-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Digital SAT Score Ranges for Top Universities</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border">University</th>
                  <th className="py-2 px-4 border">Average SAT Score</th>
                  <th className="py-2 px-4 border">25th-75th Percentile</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 px-4 border">Harvard University</td>
                  <td className="py-2 px-4 border">1520</td>
                  <td className="py-2 px-4 border">1480-1580</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">MIT</td>
                  <td className="py-2 px-4 border">1535</td>
                  <td className="py-2 px-4 border">1510-1570</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">Stanford University</td>
                  <td className="py-2 px-4 border">1505</td>
                  <td className="py-2 px-4 border">1470-1570</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">UCLA</td>
                  <td className="py-2 px-4 border">1405</td>
                  <td className="py-2 px-4 border">1300-1530</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <FAQ />
        
        <section className="my-8 bg-gray-50 p-6 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Digital SAT vs. Paper SAT: What's Different?</h2>
          <p className="mb-4">The Digital SAT introduced in 2023-2024 differs from the paper version in several key ways:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Shorter test duration (2 hours vs. 3 hours)</li>
            <li>Adaptive testing format that adjusts difficulty based on performance</li>
            <li>Two modules per section instead of longer continuous sections</li>
            <li>Digital-only format taken on a computer or tablet</li>
            <li>Faster score reporting (days instead of weeks)</li>
          </ul>
          <p className="mt-4">Our calculator is specifically designed for the new Digital SAT format, providing accurate score estimates based on the latest College Board scoring tables.</p>
        </section>
        
        <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
          <h2 className="text-xl font-semibold text-blue-800 mb-2">Need Help Improving Your SAT Score?</h2>
          <p className="mb-4">Use our free SAT score calculator to track your progress as you prepare for the Digital SAT. For personalized study plans and expert guidance, explore our SAT prep resources.</p>
          <p>Remember that this calculator provides an estimate based on official scoring tables. Your actual SAT score may vary slightly.</p>
        </div>

        <ScoreDisclaimer />
        <Footer />
      </div>
    </>
  );
}
